import type { Bootstrap } from "./api";

const PREFIX = "xflow.shell.";

/** The last bootstrap and menu of this browser. It is a paint only: the fresh answers replace it, and the server decides access (SO-4416). */
export interface KeptShell {
  uid: string;
  org: Bootstrap;
  views: string[];
  roles: string[];
  pins: string[];
}

const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === "string");

function valid(value: Partial<KeptShell> | null): value is KeptShell {
  const identity = value?.org?.identity;
  return Boolean(value?.uid && value.org?.name && identity?.api_key && identity.auth_domain) && strings(value?.views) && strings(value?.roles) && strings(value?.pins);
}

/** The kept shell of the organization, of this user when `uid` is given. Null when there is none or it cannot be read. */
export function readShell(slug: string, uid?: string): KeptShell | null {
  try {
    const kept = JSON.parse(localStorage.getItem(`${PREFIX}${slug}`) ?? "null") as Partial<KeptShell> | null;
    return valid(kept) && (uid === undefined || kept.uid === uid) ? kept : null;
  } catch {
    return null;
  }
}

/** True while `/api/me/can` has not answered and the kept shell gives the view: the page may open with its kept data. */
export const keptMay = (kept: Pick<KeptShell, "views"> | null, views: unknown, view: string): boolean => views === undefined && Boolean(kept?.views.includes(view));

export function keepShell(slug: string, shell: KeptShell): void {
  try {
    localStorage.setItem(`${PREFIX}${slug}`, JSON.stringify(shell));
  } catch {
    // Without storage, the next load starts with an empty header.
  }
}

export function dropShell(slug: string): void {
  try {
    if (localStorage.getItem(`${PREFIX}${slug}`) === null) return;
    localStorage.removeItem(`${PREFIX}${slug}`);
    console.info(`shell: dropped the kept menu of ${slug}`);
  } catch {
    // Without storage, nothing was kept.
  }
}

/** At a sign-out: no kept menu stays in this browser. */
export function dropAllShells(): void {
  try {
    const slugs = Array.from({ length: localStorage.length }, (_, n) => localStorage.key(n)).filter((key): key is string => key?.startsWith(PREFIX) ?? false);
    for (const key of slugs) dropShell(key.slice(PREFIX.length));
  } catch {
    // Without storage, nothing was kept.
  }
}
